import { useRect } from '@studio-freight/hamo'
import cn from 'clsx'

import { useScroll } from 'hooks/use-scroll'
import { Layout } from 'layouts/default'
import { button, useControls } from 'leva'
import { clamp, mapRange } from 'lib/maths'
import { useStore } from 'lib/store'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { useIntersection, useWindowSize } from 'react-use'
import s from '../home/home.module.scss'

const AppearTitle = dynamic(
  () => import('components/appear-title').then((mod) => mod.AppearTitle),
  { ssr: false }
)

const WebGL = dynamic(
  () => import('components/webgl').then(({ WebGL }) => WebGL),
  { ssr: false }
)

export default function Home() {
  const [hasScrolled, setHasScrolled] = useState()
  const zoomRef = useRef(null)
  const [zoomWrapperRect] = useRect()
  const { height: windowHeight } = useWindowSize()

  const [theme, setTheme] = useState('light')
  const lenis = useStore(({ lenis }) => lenis)

  useControls(
    'aryan',
    () => ({
      stop: button(() => {
        lenis.stop()
      }),
      start: button(() => {
        lenis.start()
      }),
    }),
    [lenis]
  )

  useControls(
    'scrollTo',
    () => ({
      immediate: button(() => {
        lenis.scrollTo(30000, { immediate: true })
      }),
      smoothDuration: button(() => {
        lenis.scrollTo(30000, { lock: true, duration: 10 })
      }),
      smooth: button(() => {
        lenis.scrollTo(30000)
      }),
      forceScrollTo: button(() => {
        lenis.scrollTo(30000, { force: true })
      }),
    }),
    [lenis]
  )

  useEffect(() => {
    if (!lenis) return

    function onClassNameChange(lenis) {
      console.log(lenis.className)
    }

    lenis.on('className change', onClassNameChange)

    return () => {
      lenis.off('className change', onClassNameChange)
    }
  }, [lenis])

  useScroll(({ scroll }) => {
    setHasScrolled(scroll > 10)
    if (!zoomWrapperRect.top) return

    const start = zoomWrapperRect.top + windowHeight * 0.5
    const end = zoomWrapperRect.top + zoomWrapperRect.height - windowHeight

    const progress = clamp(0, mapRange(start, end, scroll, 0, 1), 1)
    const center = 0.6
    const progress1 = clamp(0, mapRange(0, center, progress, 0, 1), 1)
    const progress2 = clamp(0, mapRange(center - 0.055, 1, progress, 0, 1), 1)
    setTheme(progress2 === 1 ? 'light' : 'dark')

    zoomRef.current.style.setProperty('--progress1', progress1)
    zoomRef.current.style.setProperty('--progress2', progress2)

    if (progress === 1) {
      zoomRef.current.style.setProperty('background-color', 'currentColor')
    } else {
      zoomRef.current.style.removeProperty('background-color')
    }
  })

  //removed whyRef from the first variable
  const [whyRect] = useRect()
  const [cardsRect] = useRect()
  const [whiteRect] = useRect()
  const [featuresRect] = useRect()
  const [inuseRect] = useRect()

  const addThreshold = useStore(({ addThreshold }) => addThreshold)

  useEffect(() => {
    addThreshold({ id: 'top', value: 0 })
  }, [])

  useEffect(() => {
    const top = whyRect.top - windowHeight / 2
    addThreshold({ id: 'why-start', value: top })
    addThreshold({
      id: 'why-end',
      value: top + whyRect.height,
    })
  }, [whyRect])

  useEffect(() => {
    const top = cardsRect.top - windowHeight / 2
    addThreshold({ id: 'cards-start', value: top })
    addThreshold({ id: 'cards-end', value: top + cardsRect.height })
    addThreshold({
      id: 'red-end',
      value: top + cardsRect.height + windowHeight,
    })
  }, [cardsRect])

  useEffect(() => {
    const top = whiteRect.top - windowHeight
    addThreshold({ id: 'light-start', value: top })
  }, [whiteRect])

  useEffect(() => {
    const top = featuresRect.top
    addThreshold({ id: 'features', value: top })
  }, [featuresRect])

  useEffect(() => {
    const top = inuseRect.top
    addThreshold({ id: 'in-use', value: top })
  }, [inuseRect])

  useEffect(() => {
    const top = lenis?.limit
    addThreshold({ id: 'end', value: top })
  }, [lenis?.limit])

  useScroll((e) => {
    console.log(window.scrollY, e.scroll, e.isScrolling, e.velocity, e.isLocked)
    // console.log(e.isScrolling)
  })

  const inUseRef = useRef()

  const [setIsVisible] = useState(false)
  const intersection = useIntersection(inUseRef, {
    threshold: 0.2,
  })
  useEffect(() => {
    if (intersection?.isIntersecting) {
      setIsVisible(true)
    }
  }, [intersection])

  return (
    <Layout
      theme={theme}
      seo={{
        title: 'Xeno Robotics',
        description:
          'Xeno Robotics is a pioneering engineering firm fusing state-of-the-art robotics with blockchain-driven intelligence to deliver autonomous systems that adapt, learn, and collaborate in diverse environments.',
      }}
      className={s.home}
    >
      <div className={s.canvas}>
        <WebGL />
      </div>

      <section
        className={s.why}
        style={{ marginBottom: '20vh' }}
        // data-lenis-scroll-snap-align="start"
        // ref={whyRef}
      >
        <div className="layout-grid">
          <h2 className={cn(s.sticky, 'h2')}>
            <AppearTitle>Privacy Policy</AppearTitle>
          </h2>
          <aside className={s.features}>
            <div className={s.feature}>
              <p className="p">
                At <span style={{ color: '#0097FE' }}>XENO Robotics</span> we
                value your privacy and are committed to protecting your personal
                information. This Privacy Policy outlines how we collect, use,
                store, and safeguard your data when you interact with our
                website, products, and services.
              </p>
              <br></br>
              <br></br>
              <p className="p">
                By accessing our website or using our services, you consent to
                the terms of this Privacy Policy.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Information We Collect</h3>
              {/* <PalacioLogo className={s.blogos}></PalacioLogo> */}
              <p className="p">
                We may collect the following types of information:
                <br></br>
                <br></br>
                <p>
                  <span style={{ color: '#0097FE' }}>
                    Personal Information:{' '}
                  </span>
                  Name, email address, phone number, billing and shipping
                  details, provided when creating an account, purchasing
                  products, or contacting us.
                </p>
                <br></br>
                <p>
                  <span style={{ color: '#0097FE' }}>
                    Technical Information:{' '}
                  </span>
                  IP address, browser type, operating system, and usage data
                  collected automatically via cookies or analytics tools.
                </p>
                <br></br>
                <p>
                  <span style={{ color: '#0097FE' }}>
                    Payment Information:{' '}
                  </span>
                  Processed securely through third-party providers; XENO does
                  not store sensitive financial details directly.
                </p>
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>How We Use Your Information</h3>
              {/* <FormulaLogo className={s.blogos}></FormulaLogo> */}
              <p className="p">
                Your information is used to:
                <br></br>
                <br></br>
                <li>Process and fulfill orders placed through the XENO Shop</li>
                <br />
                <li>Provide customer service and respond to inquiries</li>
                <br />
                <li>
                  Improve website functionality, performance, and security
                </li>
                <br />
                <li>
                  Notify you about updates, promotions, or changes in services
                  (with your consent)
                </li>
                <br />
                <li>Comply with regulatory and legal obligations </li>
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Data Sharing and Disclosure</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                We do not sell your personal data. We may share limited
                information with:
                <br></br>
                <br></br>
                <p style={{ marginBottom: '1.2rem' }}>
                  <span style={{ color: '#0097FE' }}>Service Providers: </span>
                  Logistics, payment processors, and IT partners necessary for
                  delivering services
                </p>
                <p style={{ marginBottom: '1.2rem' }}>
                  <span style={{ color: '#0097FE' }}>Legal Authorities: </span>
                  When required by law or regulatory obligations
                </p>
                <p className="p">
                  All third-party service providers are contractually obligated
                  to handle your data securely.
                </p>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Cookies and Tracking</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                We use cookies and similar technologies to:
                <br></br>
                <br></br>
                <li style={{ marginBottom: '1.2rem' }}>
                  Enhance your browsing experience
                </li>
                <li style={{ marginBottom: '1.2rem' }}>
                  Remember preferences and improve functionality
                </li>
                <li style={{ marginBottom: '1.2rem' }}>
                  Collect analytics to improve services
                </li>
                <p className="p">
                  You may disable cookies in your browser settings, but certain
                  website features may not function properly
                </p>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Data Security</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                We implement robust security measures to protect your data,
                including encryption, secure servers, and limited access
                controls. However, no online transmission can be guaranteed 100%
                secure.
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Your Rights</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                Depending on your jurisdiction, you may have rights to:
                <br></br>
                <br></br>
                <li style={{ marginBottom: '1.2rem' }}>
                  Access the personal data we hold about you
                </li>
                <li style={{ marginBottom: '1.2rem' }}>
                  Request corrections or deletion of your personal information
                </li>
                <li style={{ marginBottom: '1.2rem' }}>
                  Withdraw consent to marketing communications
                </li>
                <li style={{ marginBottom: '1.2rem' }}>
                  File complaints with relevant data protection authorities
                </li>
                <p className="p">
                  To exercise your rights, contact us at{' '}
                  <span style={{ color: '#0097FE' }}>support@xeno.ae.</span>
                </p>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>
                International Data Transfers
              </h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                As XENO operates in Dubai and maintains warehouse operations in
                China, your information may be transferred internationally. We
                ensure appropriate safeguards are in place to protect your data.
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Updates to this Policy</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                We may update this Privacy Policy from time to time. Any changes
                will be posted on this page with the updated effective date.
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Contact Us</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                If you have any questions or concerns about this Privacy Policy
                or how your data is handled, please contact:
                <br /><br />
                <p style={{ color: '#0097FE' }}>XENO Robotics</p>
                <p>Dubai World Trade Center (Office – TBA)</p>
                <p>Email: support@xeno.ae</p>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  )
}

export async function getStaticProps() {
  return {
    props: {
      id: 'home',
    }, // will be passed to the page component as props
  }
}
